import {Box, HStack, Text} from '@chakra-ui/react';
import {ProfilUBItem, ProfilUBItemStatutEnum} from 'api-types/ffconnect';
import {useTranslation} from 'react-i18next';
import {DisplayStatus} from '@/components/ub/StatusDisplay';

// Filtres exposés à l'utilisateur. "actives" agrège (gagne + ras) côté logique.
export type UBStatusFilter =
  | 'tout'
  | 'actives'
  | 'perdu'
  | 'en-baisse'
  | 'a-gagner';

interface Props {
  items: ProfilUBItem[] | undefined;
  selected: UBStatusFilter;
  onChange: (filter: UBStatusFilter) => void;
}

// Barre de filtres par statut "Tout / Actives / Perdu / En baisse / À gagner"
// "Actives" = gagne + ras
export const MesProduitsUBFiltersBar: React.FC<Props> = ({
  items,
  selected,
  onChange,
}) => {
  const {t} = useTranslation();
  const counts = {
    tout: items?.length ?? 0,
    actives:
      items?.filter((i) => i.statut === 'gagne' || i.statut === 'ras').length ??
      0,
    perdu: items?.filter((i) => i.statut === 'perdu').length ?? 0,
    'en-baisse': items?.filter((i) => i.statut === 'en-baisse').length ?? 0,
    'a-gagner': items?.filter((i) => i.statut === 'a-gagner').length ?? 0,
  };

  // Ordre d'affichage des filtres + mapping vers statut (pour affichage du point coloré)
  const tabs: {
    key: UBStatusFilter;
    label: string;
    statut?: ProfilUBItemStatutEnum;
  }[] = [
    {key: 'tout', label: t('client.mes_produits.ub_essentielles.filtres.tout')},
    {
      key: 'actives',
      label: t('client.mes_produits.ub_essentielles.filtres.actives', {
        count: counts.actives,
      }),
      statut: 'gagne',
    },
    {
      key: 'perdu',
      label: t('client.mes_produits.ub_essentielles.filtres.perdu', {
        count: counts.perdu,
      }),
      statut: 'perdu',
    },
    {
      key: 'en-baisse',
      label: t('client.mes_produits.ub_essentielles.filtres.en_baisse'),
      statut: 'en-baisse',
    },
    {
      key: 'a-gagner',
      label: t('client.mes_produits.ub_essentielles.filtres.a_gagner'),
      statut: 'a-gagner',
    },
  ];

  return (
    <HStack
      spacing={2}
      overflowX='auto'
      w='100%'
      py='2'
      css={{
        '&::-webkit-scrollbar': {display: 'none'},
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {tabs.map(({key, label, statut}) => {
        const isActive = selected === key;
        return (
          <Box
            key={key}
            as='button'
            onClick={() => {
              onChange(key);
            }}
            flex='1 1 0'
            minW={{base: '120px', md: '140px'}}
            h='44px'
            borderRadius='12px'
            bg={isActive ? 'blue.navy' : 'gray.light_bg'}
            color={isActive ? 'white' : 'gray.dark'}
            px='4'
            cursor='pointer'
            transition='background 0.15s'
            _hover={{bg: isActive ? 'blue.navy' : 'gray.bg'}}
          >
            <HStack
              justifyContent='center'
              spacing={2}
            >
              {statut && (
                <DisplayStatus
                  status={statut}
                  size={4}
                />
              )}
              <Text
                fontWeight='600'
                fontSize='sm'
              >
                {label}
              </Text>
              <Box
                bg='white'
                color='gray.dark'
                fontSize='10px'
                fontWeight='700'
                borderRadius='8px'
                width='20px'
                height='18px'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                {counts[key]}
              </Box>
            </HStack>
          </Box>
        );
      })}
    </HStack>
  );
};
